/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useState, useRef, useCallback } from 'preact/hooks';

const TOUCH_HOLD_THRESHOLD_MS = 200;

/**
 * Returns the event handlers for visualization hover gestures.
 */
export function useVisHover(
  onHover: (event: PointerEvent) => void,
  onHoverEnter?: (event: PointerEvent, currentTarget: HTMLElement) => void,
  onHoverLeave?: () => void,
  touchResponse: 'touchStart' | 'touchHold' = 'touchStart',
  isDisabled = false
) {
  const [isTouchHovering, setIsTouchHovering] = useState(false);
  const touchTimeoutId = useRef<ReturnType<typeof setTimeout>>();

  const onTouchMove = useCallback(
    (event: TouchEvent) => {
      // Disable browser handling of touch gestures such as panning.
      if (isTouchHovering) event.preventDefault();
    },
    [isTouchHovering]
  );

  const onPointerDown = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        // useDatatip currently depends on event.currentTarget during onHoverEnter
        // On 'touchHold', the event.currentTarget would be accessed in a setTimeout,
        // at which point it becomes null.
        // Solution for now is to store the currentTarget upfront and pass it as an
        // argument to onHoverEnter.
        // TODO: JET-68292 to improve this.
        const currentTarget = event.currentTarget as HTMLElement;
        const initiateTouchHover = () => {
          setIsTouchHovering(true);
          onHoverEnter?.(event, currentTarget);
          onHover(event);
          if ((event.target as Element).hasPointerCapture(event.pointerId)) {
            (event.target as Element).releasePointerCapture(event.pointerId);
          }
        };
        if (touchResponse === 'touchHold') {
          touchTimeoutId.current = setTimeout(initiateTouchHover, TOUCH_HOLD_THRESHOLD_MS);
        } else {
          initiateTouchHover();
        }
      }
    },
    [onHoverEnter, onHover, touchResponse]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'touch' && !isTouchHovering) return;
      onHover(event);
    },
    [onHover, isTouchHovering]
  );

  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        clearTimeout(touchTimeoutId.current);
        touchTimeoutId.current = undefined;
        if (isTouchHovering) onHoverLeave?.();
        setIsTouchHovering(false);
      }
    },
    [onHoverLeave, isTouchHovering]
  );

  const onPointerEnter = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'touch' && !isTouchHovering) return;
      onHoverEnter?.(event, event.currentTarget as HTMLElement);
    },
    [onHoverEnter, isTouchHovering]
  );

  const onPointerLeave = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'touch' && !isTouchHovering) return;
      onHoverLeave?.();
    },
    [onHoverLeave, isTouchHovering]
  );

  const onPointerCancel = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'touch' && !isTouchHovering) {
        clearTimeout(touchTimeoutId.current);
        touchTimeoutId.current = undefined;
      }
    },
    [isTouchHovering]
  );

  return isDisabled
    ? {}
    : {
        onTouchMove,
        onPointerDown,
        onPointerUp,
        onPointerMove,
        onPointerEnter,
        onPointerLeave,
        onPointerCancel
      };
}
